import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import PriceRangeSelector from "src/Components/Pharmacy/PriceRangeSelector";
import ProductsViewContainer from "src/Components/Pharmacy/ProductsViewContainer";
import SideCategoryList from "src/Components/Pharmacy/SideCategoryList";
import SideTabList from "src/Components/Pharmacy/SideTabList";
import PharmaCard from "src/Components/WebsiteComponents/HomePage/Cards/PharmaCard";
import {
  convertToApiSortKey,
  convertToKebabCase,
  decodedToken,
  generateMongoId,
  getHeaderHeight,
  parseQueryParams,
} from "src/helpers/helperFunctions";
import { categoryList, dummyDescription } from "src/lib/DummmyData";
import {
  IPharmacyObj,
  IqueryParams,
  ProductSortType,
} from "src/types/GlobalInterfaces";
import { IPaginated } from "src/types/paginated";
import qs from "qs";
import httpRequest from "src/helpers/httpRequest";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "src/Store/store";
import { capitalize } from "@mui/material";
import { ImageBaseUrl, QueryPath } from "src/lib/constans";
import { useQueryParams } from "src/hooks/useQueryParams";
import { removeAllDisLike } from "src/Store/disLike";

interface IResponse {
  documents: IPharmacyObj[];
  paginated: IPaginated;
}

interface params extends IqueryParams {
  brandId: string;
  productTagId: string;
  sortBy: ProductSortType;
}

const ProductByBrandPage = () => {
  const { userDetails } = useSelector((store: RootState) => store.appReducer);
  const { pharmacyDisLike } = useSelector((state: RootState) => state.disLike);
  const dispatch = useDispatch<AppDispatch>();
  const [brandCategories, setBrandCategories] = useState<
    { label: string; quantity: number; id: string }[]
  >([]);
  const [brandCategoriesLoading, setBrandCategoriesLoading] =
    useState<boolean>(false);
  const [selectedBrandCategory, setSelectedBrandCategory] = useState<any>(null);
  const [nextCursor, setNextCursor] = useState<{
    cursorId?: string;
    cursorPrice?: string;
    cursorKey?: string;
  }>({
    cursorId: "",
    cursorPrice: "",
    cursorKey: "",
  });
  const [currentSort, setCurrentSort] = useState<string>("Most Popular");
  const navigate = useNavigate();
  const searchParams = useQueryParams();
  const brandId = searchParams.getParam(QueryPath.brand) || "";
  const brandName = searchParams.getParam(QueryPath.brandName) || "";
  const tagId = searchParams.getParam(QueryPath.tag) || "";
  const tagName = searchParams.getParam(QueryPath.tagName) || "";

  const [isLoader, setIsLoader] = useState<boolean>(false);
  const [paginated, setPaginated] = useState<IPaginated>({
    currentPage: 1,
    totalPages: 1,
  });
  const [products, setProducts] = useState<IPharmacyObj[]>([]);
  const [breadCrumbData, setBreadCrumbData] = useState<any[]>([]);
  const [queryParams, setQueryParams] = useState<params>({
    page: 1,
    limit: 12,
    userId: decodedToken()?.id || "",
    minPrice: 0,
    maxPrice: 100000,
    brandId: "",
    productTagId: "",
    sortBy: "most-popular",
  });

  const [bannerData, setBannerData] = useState<any>(null);
  const [bannerDataLoading, setBannerDataLoading] = useState<boolean>(false);
  
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastApiCallRef = useRef<number>(0);

  const getBannerData = async () => {
    if (!brandId) return;
    setBannerDataLoading(true);
    const { res, err } = await httpRequest<{ document: any[] }>({
      path: `/brand/details/${brandId}`,
    });
    console.log("res of banner", res);
    if (res) {
      let d = res?.document[0];
      setBannerData({
        description: d?.shortDescription,
        image:  d?.bannerImage,
        logo:  d?.brandLogo,
        title: d?.brandName,
      });
    } else {
      toast(err, { type: "error" });
    }
    setBannerDataLoading(false);
  };

  const fetchBrandCategories = async (q: any, debounce: boolean = false) => {
    if (isLoader) {
      console.log("API call already in progress, skipping duplicate call");
      return;
    }
    
    if (debounce) {
      const now = Date.now();
      const timeSinceLastCall = now - lastApiCallRef.current;
      
      if (timeSinceLastCall < 500) {
        console.log("Debouncing API call - too soon after last call");
        
        if (debounceTimeoutRef.current) {
          clearTimeout(debounceTimeoutRef.current);
        }
        
        debounceTimeoutRef.current = setTimeout(() => {
          fetchBrandCategories(q, false);
        }, 500);
        
        return;
      }
      
      lastApiCallRef.current = now;
    }
    
    let newq = {
      ...q,
      brandId: brandId ? [brandId] : [],
      ...(q.productTagId && q.productTagId !== "all" ? { productTagId : [q.productTagId] } : {productTagId : undefined}),
    };

    if (
      q.sortBy === "lowToHigh" ||
      (q.sortBy === "highToLow" && newq.page > 1)
    ) {
      newq = {
        ...newq,
        ...nextCursor,
      };
    }

    let queryStr = "";
    Object.keys(newq).forEach((key) => {
      if (newq[key]) {
        queryStr += `${key}=${newq[key]}&`;
      } else {
        queryStr += `${key}=${""}&`;
      }
    });
    queryStr = queryStr.slice(0, -1);
    setIsLoader(true);
    const { res, err } = await httpRequest<IResponse>({
      path: `/pharma-inventory/home/getPharmaProducts?${qs?.stringify(newq)}`,
    });
    if (res) {
      let { documents = [], paginated } = res;
      setProducts((prev) => [...prev, ...documents]);
      setPaginated(paginated);
      dispatch(removeAllDisLike("pharmacyDisLike"));
      setNextCursor(paginated?.nextCursor || {});
    } else {
      toast(err, { type: "error" });
    }
    setIsLoader(false);
  };

  const handleQueryChange = (
    data: Partial<params>,
    update: boolean = true,
    clear: boolean = false
  ) => {
    let qP = {
      ...queryParams,
      ...data,
    };
    setQueryParams(qP);
    if (clear) {
      setProducts([]);
      qP.page = 1;
      window.scrollTo(0, 0);
    }
    if (update) {
      let q = {
        ...qP,
        sortBy: convertToApiSortKey(qP?.sortBy || "most-popular"),
      };
      fetchBrandCategories(q, true);
    }
  };

  useEffect(() => {
    if (brandId) {
      console.log("Brand Id", brandId);
      console.log("Tag Id", tagId);
      const data = [
        {
          label: "Home",
          onClick: () => navigate("/"),
        },
        {
          label: "Brands",
          onClick: () => navigate("/brands"),
        },
        {
          label: capitalize(brandName || ""),
          ...(brandName ? { onClick: () => navigate(`/brand/${convertToKebabCase(brandName)}?${parseQueryParams({
            [QueryPath.brand]: brandId,
            [QueryPath.brandName]: brandName,
          })}`) } : {}),
        },
        {
          label: capitalize(tagName || ""),
        },
      ];
      setBreadCrumbData(data.filter((item) => item.label));
      setProducts([]);
      handleQueryChange({
        brandId: brandId || "",
        productTagId: tagId || "",
      });
    }
    getBannerData();
  }, [brandId]);

  const fetchTags = async (q: any) => {
    setBrandCategoriesLoading(true);
    const { res, err } = await httpRequest<{ documents: any[] , productCount : number}>({
      path: `/inventory-favProduct/product-tags-counts?${qs?.stringify(q)}`,
    });
    if (res) {
      console.log("res of tags", res);
      if (res?.documents?.length > 0) {
        setBrandCategories([
          {
            label: "All",
            quantity: res?.productCount || 0,
            id: "all",
          },
          ...res?.documents?.map((item: any) => ({
            label: item?.tagName || "",
            quantity: item?.count || 0,
            id: item?.tagId || "",
          })),
        ]);
      } else {
        setBrandCategories([]);
      }
    } else {
      toast(err, { type: "error" });
    }
    setBrandCategoriesLoading(false);
  };

  useEffect(() => {
    if (brandId) {
      fetchTags({ brandId: brandId || "" });
    }
  }, [brandId]);

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (pharmacyDisLike.length > 0) {
      console.log("pharmacyDisLike", pharmacyDisLike);
      let updatedStatusData = products.map((v) => ({
        ...v,
        isLiked: pharmacyDisLike.find((i) => i === v?._id)
          ? false
          : v?.isLiked,
      }));
      console.log("updatedStatusData", updatedStatusData);
      setProducts(updatedStatusData);
      dispatch(removeAllDisLike("pharmacyDisLike"));
    }
  }, [pharmacyDisLike]);

  return (
    <div className="w-full">
      <div className="view-area-width flex gap-1 py-3 relative">
        <div
          className="p-[2px] product-side-bar-width custom-ai-scroll-bar !sticky overflow-y-auto"
          style={{
            height: `calc(100vh - ${getHeaderHeight()}px - 16px)`,
            top: `${getHeaderHeight() + 12}px`,
          }}
        >
          <div className="w-full box-design bg-white">
            <SideTabList
              id={"brand-category"}
              title="Brand Categories"
              List={brandCategories}
              onListClick={(item) => {
                let tagQuery = parseQueryParams({
                  [QueryPath.brand]: brandId,
                  [QueryPath.brandName]: brandName,
                  [QueryPath.tag]: item.id,
                  [QueryPath.tagName]: item.label,
                });
                navigate(
                  `/brand/${convertToKebabCase(brandName || "")}${
                    item.id && item.id !== "all"
                      ? `/${convertToKebabCase(item.label || "")}`
                      : ""
                  }?${tagQuery}`
                );
                setBreadCrumbData([
                  breadCrumbData[0],
                  breadCrumbData[1],
                  {
                    ...breadCrumbData[2],
                    onClick: () => {
                      let brandQuery = parseQueryParams({
                        [QueryPath.brand]: brandId,
                        [QueryPath.brandName]: brandName,
                      });
                      navigate(
                        `/brand/${convertToKebabCase(bannerData?.title || "")}?${brandQuery}`
                      );
                      setBreadCrumbData([
                        breadCrumbData[0],
                        breadCrumbData[1],
                        breadCrumbData[2],
                      ]);
                      handleQueryChange({
                        brandId: brandId || "",
                        productTagId: "",
                      }, true, true);
                    },
                  },
                  ...(item.id && item.id !== "all"
                    ? [
                        {
                          label: capitalize(item.label || ""),
                        },
                      ]
                    : []),
                ]);
                handleQueryChange(
                  {
                    productTagId: item.id && item.id !== "all" ? item.id : "",
                  },
                  true,
                  true
                );
              }}
              showCheckBox={false}
              showRadio={true}
              loading={brandCategoriesLoading}
              loadingText="Loading Brand Categories"
              selectedItem={
                queryParams?.productTagId === ""
                  ? "all"
                  : queryParams?.productTagId
              }
            />
            <PriceRangeSelector
              min={queryParams?.minPrice || 0}
              max={queryParams?.maxPrice || 1000000}
              onChange={(min, max) => {
                handleQueryChange({ minPrice: min, maxPrice: max }, true, true);
              }}
            />
            <SideCategoryList
              title="Categories"
              List={categoryList}
              borderBottom={false}
            />
          </div>
        </div>
        <ProductsViewContainer
          breadCrumbData={breadCrumbData}
          bannerData={bannerData}
          bannerLoading={bannerDataLoading}
          currentPage={queryParams?.page}
          totalPages={paginated?.totalPages}
          sortOptions={[
            {
              label: "Most Popular",
              value: "most-popular",
            },
            {
              label: "Most Rated",
              value: "most-rated",
            },
            {
              label: "Low to High",
              value: "price-low-to-high",
            },
            {
              label: "High to Low",
              value: "price-high-to-low",
            },
          ]}
          currentSort={queryParams?.sortBy}
          onSortChange={(sort) => {
            handleQueryChange({ sortBy: sort }, true, true);
          }}
          products={products}
          loading={isLoader}
          renderProduct={(product , index) => (
            <PharmaCard
              key={product?.id}
              className="w-full !px-0 !pb-0"
              cardClassName="w-full"
              product={product}
              index={index}
              onLikeProduct={(id, status) => {
                setProducts((prev) =>
                  prev?.map((item) => {
                    if (item?._id === id) {
                      return {
                        ...item,
                        isLiked: status,
                      };
                    }
                    return item;
                  })
                );
              }}
            />
          )}
          onLoadAgain={() => {
            handleQueryChange({
              brandId: brandId || "",
              productTagId: tagId || "",
            }, true, true);
          }}
          onLoadMore={(page) => {
            console.log("Page Number Changed to ", page);
            handleQueryChange({
              page: page,
            });
          }}
          renderSkeleton={() => (
            <PharmaCard
              className="w-full !px-0 !pb-0"
              cardClassName="w-full"
              key={"sdsd"}
              skeleton
            />
          )}
        />
      </div>
    </div>
  );
};

export default ProductByBrandPage;
